import { Row } from 'antd';
import { SelectValue } from 'antd/lib/select';
import { FormApi } from 'final-form';
import { ComponentType, FC, ReactNode, useState } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { connect } from 'react-redux';
import { Transition } from 'react-transition-group';

import { ContentLocalesI } from '~src/interfaces/session.types';
import { KeyTextObj } from '~src/interfaces/translation.interface';
import { StoreI } from '~src/rootReducer';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { getCompanyContentLocale } from '~src/services/session';
import CustomTextArea from '~src/shared/form/inputs/CustomTextArea';
import CustomFormInput from '~src/shared/form/inputs/FormInput';
import SelectInput from '~src/shared/form/select/SelectInput';
import * as SC from '~src/shared/form/translatableFormShared.sc';
import TileHeader from '~src/shared/headers/tileHeader/TileHeader';
import HoverInfoIcon from '~src/shared/tooltips/HoverInfoIcon';
import { FieldType, PartialBy } from '~src/types';

import { SectionOpt, TranslatableField } from './interfaces';
import { FieldWrapper, SectionTitle } from './translatableSectionForm.sc';
import {TFunction} from "i18next";

const duration = 200;

export type FormValuesI = Record<string, boolean | string | ContentLocalesI>;

interface DefaultProps {
  submitBtnKey: string;
  cancelBtnKey: string;
  showText: boolean;
  submitOnlyChanged: boolean;
}

interface OwnProps extends DefaultProps {
  t: TFunction;
  form: string;
  sections: SectionOpt[];
  titleKey?: string;
  title?: string;
  tooltip?: KeyTextObj;
  loadingSave: boolean;
  onCancel: () => void;
  onChange?: (values: FormValuesI) => void;
  onSubmit: (values: FormValuesI) => ApiReduxResp<unknown>;
  onToggleTranslationBox?: (show: boolean) => void;
  additionalSection?: ReactNode;
  handleSubmit: () => void;
}

export type TSFormData = {
  selectedContentLocale: string;
  isTranslationBoxVisible: boolean;
} & FormValuesI;

interface StateProps {
  defaultContentLocale: string;
  contentLocales: ContentLocalesI;
  filteredLocales: { [key: string]: string };
}

type Props = OwnProps & StateProps;

interface State {
  selectedContentLocale: string;
}

const TranslatableSectionFormRaw: FC<Props> = (props) => {
  const [state, setState] = useState({
    selectedContentLocale: props.defaultContentLocale === 'en_US' ? 'de_DE' : 'en_US',
  } as State);

  const onDropDownSelect = (value: SelectValue) => {
    setState(oldState => ({ ...oldState, selectedContentLocale: value as string }));
  };

  const renderTextInput = ({ autoFocus, input, label, labelKey, meta: { error, touched } }: FieldType) => {
    return (
      <CustomFormInput
        {...input}
        readonly={!props.showText}
        autoFocus={autoFocus}
        type="text"
        error={touched && error ? error : null}
        label={label}
        labelKey={labelKey}
      />
    );
  };

  const renderCheckbox = ({ autoFocus, input, label, labelKey, meta: { error, touched } }: FieldType) => {
    return (
      <CustomFormInput
        {...input}
        readonly={!props.showText}
        autoFocus={autoFocus}
        type="checkbox"
        error={touched && error ? error : null}
        label={label}
        labelKey={labelKey}
      />
    );
  };

  const renderTextArea = ({ autoFocus, input, label, labelKey, meta: { error, touched } }: FieldType) => (
    <FieldWrapper>
      <CustomTextArea
        {...input}
        autoFocus={autoFocus}
        type="text"
        error={touched && error ? error : null}
        label={label}
        labelKey={labelKey}
      />
    </FieldWrapper>
  );

  const renderSelectInput = ({
    input,
    label,
    labelKey,
    list,
    meta: { touched, error },
  }: FieldType & {
    list: Record<string, string>;
  }) => {
    const optList = Object.keys(list).map((k) => ({ name: list[k], value: k }));
    return (
      <SelectInput
        {...input}
        t={props.t}
        label={label}
        labelKey={labelKey}
        optList={optList}
        error={touched && error ? error : null}
        onSelect={onDropDownSelect}
      />
    );
  };

  const renderField = (field: TranslatableField, locale: string, focus = false) => {
    const isTranslation = locale !== props.defaultContentLocale;
    let renderFunc = renderTextInput;

    if (field.type === 'textArea') {
      renderFunc = renderTextArea;
    }

    if (field.type === 'checkbox') {
      renderFunc = renderCheckbox;
    }

    return (
      <Field
        key={field.name}
        name={`${field.name}.${locale}`}
        label={props.t(field.label)}
        focus={focus}
        type={field.type}
        children={renderFunc}
        labelKey={field.label}
        validate={field[isTranslation ? 'translationValidator' : 'validator'] || undefined}
      />
    );
  };

  const renderSection = (
    section: SectionOpt,
    defaultContentLocale: string,
    SecIndex: number,
    noLabel = false,
  ) => {
    const { t } = props;
    return (
      <div key={section.name}>
        {section.titleKey && (
          <SectionTitle>
            {!noLabel && (
              <>
                <span>{t(section.titleKey)}</span>
                {section.descriptionKey && (
                  <HoverInfoIcon
                    infoText={{
                      key: section.descriptionKey,
                      txt: t(section.descriptionKey),
                    }}
                  />
                )}
              </>
            )}
          </SectionTitle>
        )}
        {section.fields.map((field: TranslatableField, index) =>
          renderField(field, defaultContentLocale, SecIndex === 0 && index === 0),
        )}
      </div>
    );
  };

  const createCancel = (form: FormApi<unknown>) => {
    return () => {
      form.reset();
      props.onCancel();
    };
  };

  const { t, titleKey, sections, filteredLocales, defaultContentLocale } = props;

  const formState = useFormState();
  const form = useForm();

  const toggleTranslationBox = () => {
    form.change('isTranslationBoxVisible', !formState.values.isTranslationBoxVisible);
  };

  const isTranslationBoxVisible = formState.values.isTranslationBoxVisible as boolean;

  const submitBtnKey = props.submitBtnKey || 'x.shared.form.save_button';
  const cancelBtnKey = props.cancelBtnKey || 'x.shared.form.cancel_button';

  return (
    <form onSubmit={props.handleSubmit} noValidate id={props.form}>
      {titleKey && (
        <TileHeader title={props.title || t(titleKey)} titleKey={titleKey} tooltip={props.tooltip} border />
      )}
      <SC.FormContent>
        {props.showText && (
          <Transition in={isTranslationBoxVisible} timeout={duration}>
            {(status) => (
              <SC.FormContainer>
                <SC.DefaultContainer $duration={duration} $toggled={isTranslationBoxVisible}>
                  <SC.LeftColHeader>
                    <div>
                      <SC.DefLangTitle data-translation="x.shared.name_and_description_form.default_language">
                        {t('x.shared.name_and_description_form.default_language')}
                      </SC.DefLangTitle>
                      <SC.DefLangTxt>{props.contentLocales[defaultContentLocale]}</SC.DefLangTxt>
                    </div>
                    <SC.ToggleTranslationBtn onClick={toggleTranslationBox}>
                      {!isTranslationBoxVisible && (
                        <span data-translation="x.shared.name_and_description_form.edit_translations">
                          {t('x.shared.name_and_description_form.edit_translations')}
                        </span>
                      )}
                    </SC.ToggleTranslationBtn>
                  </SC.LeftColHeader>
                  <SC.Fieldset>
                    {sections.map((section: SectionOpt, index: number) =>
                      renderSection(section, defaultContentLocale, index),
                    )}
                  </SC.Fieldset>
                </SC.DefaultContainer>
                <SC.TranslationContainer
                  $duration={duration}
                  $status={status}
                  $toggled={isTranslationBoxVisible}
                >
                  {(status === 'entering' || status === 'exiting' || isTranslationBoxVisible) && (
                    <>
                      <SC.LeftColHeader>
                        <SC.ContentLocaleSelectBox>
                          <Field
                            name="selectedContentLocale"
                            label={t('x.users.content_language')}
                            labelKey="x.users.content_language"
                            children={renderSelectInput}
                            list={filteredLocales}
                          />
                        </SC.ContentLocaleSelectBox>
                        <SC.ToggleTranslationBtn onClick={toggleTranslationBox}>
                          {isTranslationBoxVisible && (
                            <span data-translation="x.shared.name_and_description_form.hide_translations">
                              {t('x.shared.name_and_description_form.hide_translations')}
                            </span>
                          )}
                        </SC.ToggleTranslationBtn>
                      </SC.LeftColHeader>
                      {Object.keys(filteredLocales).map((locale) => {
                        if (state.selectedContentLocale === locale) {
                          return (
                            <SC.Fieldset key={locale}>
                              {sections.map((section: SectionOpt, index: number) => (
                                <div key={section.name}>{renderSection(section, locale, index, true)}</div>
                              ))}
                            </SC.Fieldset>
                          );
                        }
                        return null;
                      })}
                    </>
                  )}
                </SC.TranslationContainer>
              </SC.FormContainer>
            )}
          </Transition>
        )}
        <Row gutter={30}>{props.children}</Row>
        <Row gutter={30}>
          <SC.ButtonsPanel>
            {(formState.dirty || !props.showText) && (
              <>
                <SC.CancelBtn type="button" onClick={createCancel(form)} data-translation={cancelBtnKey}>
                  {t(cancelBtnKey)}
                </SC.CancelBtn>
                <SC.SubmitBtn isLoading={props.loadingSave}>
                  <span data-translation={submitBtnKey}>{t(submitBtnKey)}</span>
                </SC.SubmitBtn>
              </>
            )}
          </SC.ButtonsPanel>
        </Row>
      </SC.FormContent>
    </form>
  );
};

const mapStateToProps = (state: StoreI): StateProps => {
  const defaultContentLocale = getCompanyContentLocale(state);
  const { contentLocales } = state.user.session;
  const filteredLocales = { ...contentLocales };
  delete filteredLocales[defaultContentLocale];

  return {
    defaultContentLocale,
    contentLocales,
    filteredLocales,
  };
};

const TranslatableSectionFormRawC = connect(mapStateToProps)(TranslatableSectionFormRaw) as ComponentType<
  PartialBy<OwnProps, keyof DefaultProps>
>;

export default TranslatableSectionFormRawC;
